import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  GoogleMap,
  InfoWindow,
  Marker,
  Circle,
  useJsApiLoader,
} from "@react-google-maps/api";
import { FaSearch } from "react-icons/fa";
import { apiRequest } from "../lib/apiClient";
const GOOGLE_MAPS_LIBRARIES = [];

const PAGE_SIZE = 12;
const FILTER_STORAGE_KEY = "rh_web_renter_filters_v1";
const DEFAULT_RADIUS = 1000; // metres
const MIN_RADIUS = 100;
const MAX_RADIUS = 15000;
const SEARCH_DEBOUNCE_MS = 350;
const CARD_REVEAL_DELAY_MS = 1000;
/* Kathmandu default centre */
const DEFAULT_CENTER = { lat: 27.7105, lng: 85.3488 };

function asCurrency(value) {
  if (typeof value !== "number") return "Price unavailable";
  return new Intl.NumberFormat("en-NP", {
    style: "currency",
    currency: "NPR",
    maximumFractionDigits: 0,
  }).format(value);
}

function buildQuery(params) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      qs.set(key, String(value));
    }
  });
  return qs.toString();
}

function readStoredFilters() {
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

export default function RenterSearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  /* ---- View mode ---- */
  const [viewMode, setViewMode] = useState("LIST"); // 'LIST' | 'MAP'

  /* ---- Geo mode ---- */
  const [geoMode, setGeoMode] = useState("CITY"); // 'CITY' | 'RADIUS'
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [radiusMeters, setRadiusMeters] = useState(DEFAULT_RADIUS);

  /* ---- Google Maps loader ---- */
  const { isLoaded: mapsLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY || "",
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  /* ---- Selected marker (map popup) ---- */
  const [selectedId, setSelectedId] = useState(null);
  const mapRef = useRef(null);
  const searchDebounceRef = useRef(null);

  /* ---- All-results map markers (fetched without pagination) ---- */
  const [mapMarkerProperties, setMapMarkerProperties] = useState([]);
  const [mapMarkersLoading, setMapMarkersLoading] = useState(false);

  const [filters, setFilters] = useState({
    city: searchParams.get("city") || readStoredFilters()?.city || "",
    minPrice:
      searchParams.get("minPrice") || readStoredFilters()?.minPrice || "",
    maxPrice:
      searchParams.get("maxPrice") || readStoredFilters()?.maxPrice || "",
    bedrooms:
      searchParams.get("bedrooms") || readStoredFilters()?.bedrooms || "",
    propertyType:
      searchParams.get("propertyType") ||
      readStoredFilters()?.propertyType ||
      "",
  });
  const [sortBy, setSortBy] = useState("recommended");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [properties, setProperties] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [listRevealReady, setListRevealReady] = useState(false);
  const [eagerCardCount, setEagerCardCount] = useState(1);
  const eagerLoadedIdsRef = useRef(new Set());
  const listRevealTimeoutRef = useRef(null);

  const queryForApi = useMemo(
    () => ({
      ...(geoMode === "CITY"
        ? { city: filters.city.trim() }
        : {
            lat: mapCenter.lat,
            lng: mapCenter.lng,
            radius: radiusMeters,
          }),
      minPrice: filters.minPrice ? Number(filters.minPrice) : undefined,
      maxPrice: filters.maxPrice ? Number(filters.maxPrice) : undefined,
      bedrooms: filters.bedrooms ? Number(filters.bedrooms) : undefined,
      propertyType: filters.propertyType || undefined,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, geoMode, mapCenter, radiusMeters],
  );

  const fetchPage = useCallback(
    async (nextPage, append, queryOverride) => {
      if (!append) {
        setListRevealReady(false);
        if (listRevealTimeoutRef.current) {
          clearTimeout(listRevealTimeoutRef.current);
          listRevealTimeoutRef.current = null;
        }
      }

      setLoading(true);
      setError("");

      try {
        const querySource = queryOverride || queryForApi;
        const queryString = buildQuery({
          ...querySource,
          page: nextPage,
          limit: PAGE_SIZE,
        });
        const payload = await apiRequest(
          `/properties/search/advanced?${queryString}`,
        );
        const incoming = Array.isArray(payload) ? payload : [];

        setProperties((prev) => (append ? [...prev, ...incoming] : incoming));
        setHasMore(incoming.length >= PAGE_SIZE);
        setPage(nextPage);
      } catch (err) {
        setError(err?.message || "Failed to load properties.");
        if (!append) {
          setProperties([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [queryForApi],
  );

  const fetchAllForMap = useCallback(async () => {
    setMapMarkersLoading(true);
    try {
      const qs = buildQuery({ ...queryForApi, limit: 200 });
      const payload = await apiRequest(`/properties/search/advanced?${qs}`);
      const incoming = Array.isArray(payload) ? payload : [];
      setMapMarkerProperties(
        incoming.filter((p) => {
          const lat = parseFloat(p.latitude);
          const lng = parseFloat(p.longitude);
          return isFinite(lat) && isFinite(lng);
        }),
      );
    } catch {
      setMapMarkerProperties([]);
    } finally {
      setMapMarkersLoading(false);
    }
  }, [queryForApi]);

  const triggerImmediateSearch = useCallback(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    fetchPage(1, false, queryForApi);
  }, [fetchPage, queryForApi]);

  useEffect(() => {
    const queryString = buildQuery(queryForApi);
    setSearchParams(queryString, { replace: true });
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      fetchPage(1, false, queryForApi);
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [fetchPage, queryForApi, setSearchParams, filters]);

  /* Refresh map markers whenever the map is visible or filters change */
  useEffect(() => {
    if (viewMode === "MAP") fetchAllForMap();
  }, [viewMode, fetchAllForMap]);

  /* Clear selected pin when search criteria changes */
  useEffect(() => {
    setSelectedId(null);
  }, [queryForApi]);

  const displayedProperties = useMemo(() => {
    const sorted = [...properties];
    if (sortBy === "priceAsc") {
      sorted.sort((a, b) => (a?.price ?? Infinity) - (b?.price ?? Infinity));
    }
    if (sortBy === "priceDesc") {
      sorted.sort((a, b) => (b?.price ?? -Infinity) - (a?.price ?? -Infinity));
    }
    if (sortBy === "bedroomsDesc") {
      sorted.sort(
        (a, b) => (b?.bedrooms ?? -Infinity) - (a?.bedrooms ?? -Infinity),
      );
    }
    return sorted;
  }, [properties, sortBy]);

  const onFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const onReset = () => {
    setFilters({
      city: "",
      minPrice: "",
      maxPrice: "",
      bedrooms: "",
      propertyType: "",
    });
    setGeoMode("CITY");
    setMapCenter(DEFAULT_CENTER);
    setRadiusMeters(DEFAULT_RADIUS);
  };

  const clearOneFilter = (key) => {
    setFilters((current) => ({ ...current, [key]: "" }));
  };

  /* ---- Geo helpers ---- */
  const switchToRadius = () => {
    setGeoMode("RADIUS");
    setViewMode("MAP");
    // attempt to get user location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          setMapCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {},
        { timeout: 4000 },
      );
    }
  };

  const switchToCity = () => {
    setGeoMode("CITY");
  };

  /* Google Maps onClick gives a MouseEvent with .latLng (methods, not props) */
  const onMapClick = (e) => {
    if (geoMode === "RADIUS" && e?.latLng) {
      setMapCenter({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    }
  };

  const onMarkerPress = (id) =>
    setSelectedId((prev) => (prev === id ? null : id));

  const mapMarkers = useMemo(
    () =>
      mapMarkerProperties
        .map((property) => {
          const lat = parseFloat(property.latitude);
          const lng = parseFloat(property.longitude);
          if (!isFinite(lat) || !isFinite(lng)) return null;
          return { id: property.id, lat, lng, property };
        })
        .filter(Boolean),
    [mapMarkerProperties],
  );

  const defaultMarkerIcon = useMemo(() => {
    if (!mapsLoaded || !window.google?.maps) return null;
    return {
      url: process.env.PUBLIC_URL + "/assets/logo/noBG_logo.png",
      scaledSize: new window.google.maps.Size(32, 32),
      anchor: new window.google.maps.Point(16, 32),
    };
  }, [mapsLoaded]);

  const selectedMarkerIcon = useMemo(() => {
    if (!mapsLoaded || !window.google?.maps) return null;
    return {
      url: process.env.PUBLIC_URL + "/assets/logo/noBG_logo.png",
      scaledSize: new window.google.maps.Size(44, 44),
      anchor: new window.google.maps.Point(22, 44),
    };
  }, [mapsLoaded]);

  const selectedProperty = selectedId
    ? (mapMarkers.find((m) => m.id === selectedId)?.property ?? null)
    : null;

  const onViewDetails = (id) => {
    navigate(`/app/properties/${id}`);
  };

  /* ---- active filter chips ---- */
  const allActiveFilters = useMemo(() => {
    const chips = [];
    if (geoMode === "CITY" && filters.city)
      chips.push({ key: "city", label: `City: ${filters.city}` });
    if (geoMode === "RADIUS")
      chips.push({
        key: "_radius",
        label: `Radius: ${(radiusMeters / 1000).toFixed(1)} km`,
      });
    if (filters.minPrice)
      chips.push({ key: "minPrice", label: `Min: Rs.${filters.minPrice}` });
    if (filters.maxPrice)
      chips.push({ key: "maxPrice", label: `Max: Rs.${filters.maxPrice}` });
    if (filters.bedrooms)
      chips.push({ key: "bedrooms", label: `${filters.bedrooms} bd+` });
    if (filters.propertyType)
      chips.push({ key: "propertyType", label: filters.propertyType });
    return chips;
  }, [geoMode, filters, radiusMeters]);

  const clearChip = (key) => {
    if (key === "_radius") {
      setGeoMode("CITY");
    } else {
      clearOneFilter(key);
    }
  };

  useEffect(() => {
    if (loading) return;

    if (!displayedProperties.length) {
      setListRevealReady(true);
      return;
    }

    if (listRevealReady) return;

    listRevealTimeoutRef.current = setTimeout(() => {
      setListRevealReady(true);
      listRevealTimeoutRef.current = null;
    }, CARD_REVEAL_DELAY_MS);

    return () => {
      if (listRevealTimeoutRef.current) {
        clearTimeout(listRevealTimeoutRef.current);
        listRevealTimeoutRef.current = null;
      }
    };
  }, [loading, displayedProperties.length, listRevealReady]);

  useEffect(() => {
    eagerLoadedIdsRef.current.clear();
    setEagerCardCount(1);
  }, [displayedProperties]);

  const onCardImageSettled = useCallback((propertyId) => {
    if (eagerLoadedIdsRef.current.has(propertyId)) {
      return;
    }
    eagerLoadedIdsRef.current.add(propertyId);
    setEagerCardCount((current) => Math.min(4, current + 1));
  }, []);

  return (
    <main className="rental-listing-page">
      {/* Hero */}
      <section className="hero-search-section">
        <img
          src={process.env.PUBLIC_URL + "/assets/logo/noBG_logo.png"}
          alt=""
          className="hero-search-watermark"
          aria-hidden="true"
        />
        <div className="hero-content">
          <h1 className="hero-title">Find your perfect rental</h1>
          <p className="hero-subtitle">
            Explore apartments and homes across Nepal
          </p>

          <div className="unified-search-bar home-hero-search-bar">
            <div className="search-input-group search-city-group">
              <label className="search-input-label">
                <input
                  name="city"
                  value={geoMode === "RADIUS" ? "" : filters.city}
                  onChange={onFilterChange}
                  disabled={geoMode === "RADIUS"}
                  placeholder={
                    geoMode === "RADIUS" ? "Using map radius…" : "City Name..."
                  }
                  className="search-input-field"
                />
              </label>
            </div>
            <div className="search-bar-divider" />
            <div className="search-input-group search-price-group">
              <label className="search-input-label">
                <input
                  name="minPrice"
                  type="number"
                  value={filters.minPrice}
                  onChange={onFilterChange}
                  placeholder="Min Price"
                  className="search-input-field"
                />
              </label>
            </div>
            <div className="search-bar-divider" />
            <div className="search-input-group search-price-group">
              <label className="search-input-label">
                <input
                  name="maxPrice"
                  type="number"
                  value={filters.maxPrice}
                  onChange={onFilterChange}
                  placeholder="Max Price"
                  className="search-input-field"
                />
              </label>
            </div>
            <button
              type="button"
              className="btn btn-search"
              onClick={triggerImmediateSearch}
              aria-label="Search"
            >
              <FaSearch className="btn-search-icon" aria-hidden="true" />
              <span className="btn-search-text">Search</span>
            </button>
          </div>
        </div>
      </section>

      {/* Results container */}
      <div className="results-container">
        {/* Active filter chips */}
        {!!allActiveFilters.length && (
          <section
            className="active-filters-section"
            aria-label="Active filters"
          >
            <div className="filters-header">
              <p className="filters-label">Active filters:</p>
              <button
                className="btn btn-tertiary btn-sm"
                type="button"
                onClick={onReset}
              >
                Clear all
              </button>
            </div>
            <div className="chip-row">
              {allActiveFilters.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  className="filter-chip"
                  onClick={() => clearChip(key)}
                >
                  {label} ✕
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Toolbar */}
        <section className="results-toolbar">
          <div className="results-info">
            <p className="results-count">
              {displayedProperties.length} result
              {displayedProperties.length === 1 ? "" : "s"}
              {viewMode === "MAP" && mapMarkerProperties.length > 0 && (
                <span className="results-count-sub">
                  {" "}
                  · {mapMarkerProperties.length} on map
                </span>
              )}
            </p>
          </div>
          <div className="toolbar-right">
            {viewMode === "LIST" && (
              <label className="sort-control">
                <span className="sort-label">Sort by:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="sort-select"
                >
                  <option value="recommended">Recommended</option>
                  <option value="priceAsc">Price: Low to High</option>
                  <option value="priceDesc">Price: High to Low</option>
                  <option value="bedroomsDesc">Most Bedrooms</option>
                </select>
              </label>
            )}

            {/* Radius toggle */}
            <button
              type="button"
              className={`view-toggle-btn${geoMode === "RADIUS" ? " view-toggle-btn--active" : ""}`}
              onClick={geoMode === "RADIUS" ? switchToCity : switchToRadius}
              title={
                geoMode === "RADIUS"
                  ? "Switch to city search"
                  : "Search by radius on map"
              }
            >
              <span className="view-toggle-icon">◎</span>
              {geoMode === "RADIUS" ? "City search" : "Radius"}
            </button>

            {/* List / Map toggle */}
            <button
              type="button"
              className={`view-toggle-btn${viewMode === "MAP" ? " view-toggle-btn--active" : ""}`}
              onClick={() =>
                setViewMode((m) => (m === "LIST" ? "MAP" : "LIST"))
              }
            >
              <span className="view-toggle-icon">
                {viewMode === "LIST" ? "🗺" : "☰"}
              </span>
              {viewMode === "LIST" ? "Map" : "List"}
            </button>
          </div>
        </section>

        {/* Radius control bar (only in MAP + RADIUS mode) */}
        {viewMode === "MAP" && geoMode === "RADIUS" && (
          <div className="radius-control-bar">
            <span className="radius-control-label">
              Radius: <strong>{(radiusMeters / 1000).toFixed(1)} km</strong>
            </span>
            <input
              type="range"
              min={MIN_RADIUS}
              max={MAX_RADIUS}
              step={100}
              value={radiusMeters}
              onChange={(e) => setRadiusMeters(Number(e.target.value))}
              className="radius-slider"
            />
            <button
              type="button"
              className="btn btn-search btn-sm"
              onClick={triggerImmediateSearch}
            >
              Search area
            </button>
            <span className="radius-control-hint">
              Click map to move center
            </span>
          </div>
        )}

        {error && <p className="form-error">{error}</p>}

        {/* MAP VIEW */}
        {viewMode === "MAP" ? (
          <div className="map-explore-container">
            {!mapsLoaded ? (
              <div className="map-loading-overlay">Loading Google Maps…</div>
            ) : (
              <GoogleMap
                mapContainerClassName="map-explore-leaflet"
                center={{ lat: mapCenter.lat, lng: mapCenter.lng }}
                zoom={13}
                onClick={onMapClick}
                options={{
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                  gestureHandling: "greedy",
                }}
                onLoad={(map) => {
                  mapRef.current = map;
                }}
              >
                {geoMode === "RADIUS" && (
                  <Circle
                    center={{ lat: mapCenter.lat, lng: mapCenter.lng }}
                    radius={radiusMeters}
                    options={{
                      strokeColor: "#004278",
                      strokeOpacity: 0.7,
                      strokeWeight: 2,
                      fillColor: "#004278",
                      fillOpacity: 0.12,
                    }}
                  />
                )}

                {mapMarkers.map((marker) => {
                  const isSelected = marker.id === selectedId;
                  return (
                    <Marker
                      key={marker.id}
                      position={{ lat: marker.lat, lng: marker.lng }}
                      onClick={() => onMarkerPress(marker.id)}
                      icon={isSelected ? selectedMarkerIcon : defaultMarkerIcon}
                      zIndex={isSelected ? 1000 : 1}
                    />
                  );
                })}

                {selectedProperty && (
                  <InfoWindow
                    position={{
                      lat: parseFloat(selectedProperty.latitude),
                      lng: parseFloat(selectedProperty.longitude),
                    }}
                    onCloseClick={() => setSelectedId(null)}
                  >
                    <div className="map-info-window">
                      {selectedProperty.images?.[0]?.url && (
                        <img
                          src={selectedProperty.images[0].url}
                          alt={selectedProperty.title || "Property"}
                          className="map-iw-img"
                        />
                      )}
                      <p className="map-iw-title">
                        {selectedProperty.title || "Untitled"}
                      </p>
                      <p className="map-iw-location">
                        {selectedProperty.location ||
                          selectedProperty.city ||
                          "—"}
                      </p>
                      <div className="map-iw-tags">
                        {selectedProperty.price != null && (
                          <span className="map-iw-tag">
                            {asCurrency(selectedProperty.price)}
                          </span>
                        )}
                        {selectedProperty.bedrooms != null && (
                          <span className="map-iw-tag">
                            {selectedProperty.bedrooms} bd
                          </span>
                        )}
                        {selectedProperty.bathrooms != null && (
                          <span className="map-iw-tag">
                            {selectedProperty.bathrooms} ba
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="map-iw-btn"
                        onClick={() => onViewDetails(selectedProperty.id)}
                      >
                        View details →
                      </button>
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            )}

            {(loading || mapMarkersLoading) && (
              <div className="map-loading-overlay">Loading…</div>
            )}
          </div>
        ) : (
          /* LIST VIEW */
          <>
            <section className="property-grid" aria-live="polite">
              {(listRevealReady ? displayedProperties : []).map(
                (property, index) => {
                  const cover = property.images?.[0]?.url;
                  const inSequentialBatch = index < 4;
                  const shouldEagerLoad =
                    inSequentialBatch && index < eagerCardCount;
                  return (
                    <article
                      className="property-card"
                      key={property.id}
                      onClick={() => onViewDetails(property.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) =>
                        e.key === "Enter" && onViewDetails(property.id)
                      }
                    >
                      <div className="property-image-container">
                        {cover ? (
                          <img
                            className="property-image"
                            src={cover}
                            alt={property.title || "Property"}
                            loading={shouldEagerLoad ? "eager" : "lazy"}
                            decoding="async"
                            fetchPriority={shouldEagerLoad ? "high" : "low"}
                            width="640"
                            height="400"
                            onLoad={() => {
                              if (inSequentialBatch) {
                                onCardImageSettled(property.id);
                              }
                            }}
                            onError={() => {
                              if (inSequentialBatch) {
                                onCardImageSettled(property.id);
                              }
                            }}
                          />
                        ) : (
                          <div className="property-image-placeholder">
                            <span>📷</span>
                          </div>
                        )}
                        <button
                          className="wish-button"
                          type="button"
                          title="Add to favorites"
                        >
                          ♡
                        </button>
                      </div>
                      <div className="property-details">
                        <h3 className="property-title">
                          {property.title || "Untitled property"}
                        </h3>
                        <p className="property-location">
                          {property.location ||
                            property.city ||
                            "Location unavailable"}
                        </p>
                        <div className="property-tag-row">
                          {property.price != null && (
                            <span className="property-tag">
                              {asCurrency(property.price)}
                            </span>
                          )}
                          {property.bedrooms != null && (
                            <span className="property-tag">
                              {property.bedrooms} bd
                            </span>
                          )}
                          {property.bathrooms != null && (
                            <span className="property-tag">
                              {property.bathrooms} ba
                            </span>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                },
              )}
            </section>

            {loading && <p className="loading-message">Loading properties…</p>}

            {!loading && !listRevealReady && displayedProperties.length > 0 && (
              <p className="loading-message">Preparing property cards…</p>
            )}

            {!loading && displayedProperties.length === 0 && (
              <div className="empty-state">
                <p>No properties found matching your criteria.</p>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={onReset}
                >
                  Try different filters
                </button>
              </div>
            )}

            {hasMore && !loading && displayedProperties.length > 0 && (
              <section className="load-more-wrap">
                <button
                  type="button"
                  className="btn btn-outline"
                  disabled={loading}
                  onClick={() => fetchPage(page + 1, true)}
                >
                  Load more properties
                </button>
              </section>
            )}
          </>
        )}
      </div>
    </main>
  );
}
